// =============================================================================
// Module Project - Service: Notification Subscription
// Gère les subscriptions realtime Supabase pour les notifications
// Seul fichier autorisé à utiliser supabase.channel() pour les notifications
// =============================================================================

import { supabase } from '@/integrations/supabase/client';
import type { ProjectNotification } from './notificationRepository';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────
export interface NotificationSubscriptionParams {
  orgId: string;
  projectId?: string;
}

export type OnNewNotificationCallback = (notification: ProjectNotification) => void;

// ─────────────────────────────────────────────────────────────────────────────
// MAP RAW PAYLOAD TO NOTIFICATION
// ─────────────────────────────────────────────────────────────────────────────
function mapPayloadToNotification(payload: Record<string, unknown>): ProjectNotification {
  return {
    id: payload.id as string,
    orgId: payload.org_id as string,
    projectId: payload.project_id as string,
    recipientMemberId: payload.recipient_member_id as string,
    type: payload.type as ProjectNotification['type'],
    entityType: payload.entity_type as ProjectNotification['entityType'],
    entityId: payload.entity_id as string,
    payload: (payload.payload as Record<string, unknown>) || {},
    isRead: payload.is_read as boolean,
    createdAt: payload.created_at as string,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// SUBSCRIBE TO NOTIFICATIONS
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Subscribe to realtime notifications for a user.
 * Returns an unsubscribe function to cleanup the channel.
 * 
 * @param params - orgId and optional projectId filter
 * @param onNewNotification - callback when a new notification arrives
 * @returns unsubscribe function
 */
export function subscribeToNotifications(
  params: NotificationSubscriptionParams,
  onNewNotification: OnNewNotificationCallback
): () => void {
  const { orgId, projectId } = params;

  const channel = supabase
    .channel(`notifications-${orgId}-${projectId || 'all'}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'project_notifications',
      },
      (payload) => {
        const rawNotification = payload.new as Record<string, unknown>;
        
        // Filter by project if specified
        if (projectId && rawNotification.project_id !== projectId) {
          return;
        }
        
        // RLS will already filter by recipient, but we can double-check org
        if (rawNotification.org_id !== orgId) {
          return;
        }

        const notification = mapPayloadToNotification(rawNotification);
        onNewNotification(notification);
      }
    )
    .subscribe();

  // Return unsubscribe function
  return () => {
    supabase.removeChannel(channel);
  };
}
